import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDashboard, useDataStatus } from "../lib/queries";
import { ago, date as fmtDate, datetime, isStale, money, num, signed } from "../lib/format";
import { useNow } from "../lib/useNow";
import { usePrefs } from "../lib/prefs";
import { CategoryBars, Sparkline, TrendChart } from "../components/charts";
import { Button, ErrorState, PageHeader, Panel, Skeleton, cn } from "../components/ui";
import { Icon } from "../lib/icons";
import { greeting } from "../lib/decor";
import { useCountUp } from "../lib/useCountUp";
import { ApiError } from "../lib/api";
import type { DashboardData, DashMover, DashStockItem, StockRiskItem, TrendPoint } from "../lib/types";
import { StockHealthCard } from "../components/StockHealth";
import { ActivityCalendar } from "../components/ActivityCalendar";

export function Overview() {
  const navigate = useNavigate();
  const prefs = usePrefs();
  const { data, isLoading, error, refetch } = useDashboard(prefs.ovTrendDays);

  const full = new Set(["kpis", "movement", "trend", "today", "stock_health", "calendar"]);

  return (
    <>
      <Hero days={prefs.ovTrendDays} onSettings={() => navigate("/settings")} />

      {error ? (
        <Panel>
          <ErrorState message={error instanceof ApiError ? error.message : String(error)} onRetry={() => refetch()} />
        </Panel>
      ) : (
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {prefs.ovCards.map((id) => (
          <div key={id} className={full.has(id) ? "lg:col-span-2" : ""}>
            <Card id={id} data={data} loading={isLoading} prefs={prefs} navigate={navigate} />
          </div>
        ))}
      </div>
      )}
    </>
  );
}

/** Page header — title, greeting + period subtitle, and the data-freshness timestamp (2026-09-12,
 * "yangilanish vaqtini ichida korsatsin"; no live clock — "soat kerak emas qachon yangilanganligi
 * korinsin"). 2026-09-28 enterprise pass: standard PageHeader, same content. */
function Hero({ days, onSettings }: { days: number; onSettings: () => void }) {
  const { data: status } = useDataStatus();
  const dateLabel = new Date().toLocaleDateString("ru-RU", { day: "numeric", month: "long", weekday: "long" });
  const when = status?.last_success || status?.last_update;
  const running = status?.state === "RUNNING";
  const now = useNow();
  const stale = !running && isStale(when, now);

  return (
    <PageHeader
      eyebrow={<span className="capitalize">{dateLabel}</span>}
      title="Обзор склада"
      subtitle={
        <>
          {greeting()}! Сводка за последние {days} дней ·{" "}
          <button type="button" onClick={onSettings} className="font-medium text-accent hover:underline hover:underline-offset-2">
            настроить
          </button>
        </>
      }
      actions={
        <>
        <Button onClick={() => window.print()} className="hidden sm:inline-flex print:hidden" title="Печать или сохранение в PDF">
          <Icon name="download" size={16} />
          Печать / PDF
        </Button>
        <div
          className={cn(
            "flex items-center gap-2.5 rounded-lg border px-3 py-2 shadow-card",
            stale ? "border-warn/40 bg-warn-weak" : "border-border bg-surface",
          )}
          title={when ? `Последнее успешное обновление\n${datetime(when)}` : undefined}
        >
          <span className="relative flex h-2 w-2 flex-none">
            {running && <span className="absolute inset-0 animate-ping rounded-full bg-accent/60" />}
            <span
              className={cn(
                "relative h-2 w-2 rounded-full",
                running ? "bg-accent" : stale ? "bg-warn" : when ? "bg-ok" : "bg-muted",
              )}
            />
          </span>
          <div className="text-sm leading-tight">
            <span className={cn("font-medium", stale && "text-warn")}>
              {running
                ? "Данные обновляются…"
                : when
                  ? `${stale ? "Данные устарели" : "Обновлено"} ${ago(when, now)}`
                  : "Нет данных"}
            </span>
            <span className="text-text-2">
              {status?.next_run && !running
                ? ` · следующее в ${hhmm(status.next_run)}`
                : ` · каждые ${status?.interval_hours ?? 2} часа`}
            </span>
          </div>
        </div>
        </>
      }
    />
  );
}

function Card({
  id,
  data,
  loading,
  prefs,
  navigate,
}: {
  id: string;
  data: DashboardData | undefined;
  loading: boolean;
  prefs: ReturnType<typeof usePrefs>;
  navigate: (to: string) => void;
}) {
  if (loading || !data) {
    if (id === "kpis")
      return (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[138px] rounded-xl" />
          ))}
        </div>
      );
    return <Skeleton className="h-[220px] rounded-xl" />;
  }
  const k = data.kpis;

  switch (id) {
    case "kpis":
      return (
        <div className={cn("grid grid-cols-2 gap-3 max-sm:grid-flow-row-dense sm:gap-4 md:grid-cols-3", KPI_COLS[prefs.ovKpis.length] ?? "xl:grid-cols-6")}>
          {(() => {
            // Phones: 2 per row. Long sums take a full row (Kpi decides); dense flow backfills the
            // gap they leave, and if the remaining tiles are odd the last one spans too — no holes.
            const tiles = prefs.ovKpis.map((kid) => ({ kid, t: kpiTile(kid, data, prefs.ovTrendDays) })).filter((x) => x.t);
            const narrow = tiles.filter((x) => x.t!.fmt(x.t!.raw).length <= 10);
            const lastNarrow = narrow.length % 2 ? narrow[narrow.length - 1].kid : null;
            return tiles.map(({ kid, t }) => (
              <Kpi key={kid} {...t!} wideOnPhone={kid === lastNarrow} onClick={t!.go ? () => navigate(t!.go!) : undefined} />
            ));
          })()}
        </div>
      );

    case "movement":
      return (
        <Panel title={`Движение за ${prefs.ovTrendDays} дней`}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat
              label="Поступило"
              value={num(data.movement.supply)}
              sub={money(data.movement.supply_value)}
              spark={data.trend.points.map((p) => p.supply)}
            />
            <Stat
              label="Отгружено"
              value={num(data.movement.shipment)}
              sub={money(data.movement.shipment_value)}
              spark={data.trend.points.map((p) => p.shipment)}
            />
            <Stat label="Брак" value={num(data.movement.defect)} tone={data.movement.defect ? "danger" : undefined} />
            <Stat label="Возврат" value={num(data.movement.return_qty)} />
          </div>
        </Panel>
      );

    case "trend":
      return (
        <Panel title="График движения" actions={<span className="text-xs text-text-2">Поставка / Отгрузка по дням</span>}>
          <TrendCard points={data.trend.points} />
        </Panel>
      );

    case "calendar":
      return <ActivityCalendar />;

    case "stock_health":
      return data.health ? <StockHealthCard h={data.health} /> : null;

    case "stockout_risk": {
      const h = data.health;
      if (!h) return null;
      return (
        <Panel
          title="Риск дефицита"
          actions={
            <span className="text-xs text-text-2">
              {num(h.stockout.total)} тов. на &lt; {h.stockout.threshold_days} дн
              {h.stockout.out > 0 && <span className="font-medium text-danger"> · {num(h.stockout.out)} закончились</span>}
            </span>
          }
        >
          <MiniList
            rows={h.stockout.items}
            empty="Запаса хватает по всем продаваемым товарам"
            render={(r: StockRiskItem) => ({
              name: r.product_name,
              meta: [r.brand, r.category].filter(Boolean).join(" · "),
              value: r.stock <= 0 ? "закончился" : `≈ ${coverLabel(r.days_cover)}`,
              danger: r.stock <= 0 || r.days_cover < 3,
              warn: r.stock > 0 && r.days_cover >= 3,
              sub: `остаток ${num(r.stock)} · ~${fmtRate(r.q30 / 30)}/день`,
              go: `/product/${encodeURIComponent(r.product_id)}`,
            })}
            navigate={navigate}
          />
        </Panel>
      );
    }

    case "today":
      return (
        <Panel
          title="Последний день с движением"
          actions={<span className="text-xs text-text-2">{fmtDate(data.today.date)}</span>}
        >
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Поступило" value={num(data.today.supply)} />
            <Stat label="Отгружено" value={num(data.today.shipment)} />
            <Stat label="Брак" value={num(data.today.defect)} tone={data.today.defect ? "danger" : undefined} />
            <Stat label="Товаров в движении" value={num(data.today.products)} />
          </div>
        </Panel>
      );

    case "top_shipped":
    case "top_supplied": {
      const rows = id === "top_shipped" ? data.top_shipped : data.top_supplied;
      return (
        <Panel title={id === "top_shipped" ? "Больше всего отгружено" : "Больше всего поступило"}>
          <MiniList
            rows={rows}
            empty="Нет движения за период"
            render={(r: DashMover) => ({
              name: r.product_name,
              meta: [r.brand, r.category].filter(Boolean).join(" · "),
              value: `${num(r.qty)} шт`,
              sub: r.value != null ? money(r.value) : undefined,
              go: `/product/${encodeURIComponent(r.product_id)}`,
            })}
            navigate={navigate}
          />
        </Panel>
      );
    }

    case "dead_stock":
      return (
        <Panel
          title="Залежавшийся товар"
          actions={
            <span className="text-xs text-text-2">
              {num(data.dead_stock.products)} тов. · {money(data.dead_stock.value)} заморожено
            </span>
          }
        >
          <MiniList
            rows={data.dead_stock.items}
            empty="Нет залежавшегося товара"
            render={(r: DashStockItem) => ({
              name: r.product_name,
              meta: [r.brand, r.category].filter(Boolean).join(" · "),
              value: idleLabel(r.days_idle),
              danger: (r.days_idle ?? 0) >= 90,
              sub: `${r.stock_value != null ? money(r.stock_value) : "—"} · ${num(r.closing_stock)} шт`,
              go: `/product/${encodeURIComponent(r.product_id)}`,
            })}
            navigate={navigate}
          />
        </Panel>
      );

    case "shipment_categories":
      return (
        <Panel
          title="Отгрузка по категориям"
          actions={<span className="text-xs text-text-2">за 30 дней · {prefs.ovCatMetric === "value" ? "по сумме" : "по количеству"}</span>}
        >
          <CategoryBars
            rows={shipCatRows(data, prefs.ovCatMetric)}
            total={catTotal(data.shipment_categories, prefs.ovCatMetric)}
            fmt={prefs.ovCatMetric === "value" ? money : num}
            onPick={(c) => navigate(`/report/current?q=${encodeURIComponent(c)}`)}
          />
        </Panel>
      );

    case "categories":
      return (
        <Panel
          title="Остаток по категориям"
          actions={
            <span className="text-xs text-text-2">{prefs.ovCatMetric === "value" ? "по сумме" : "по количеству"}</span>
          }
        >
          <CategoryBars
            rows={catRows(data, prefs.ovCatMetric)}
            total={catTotal(data.categories, prefs.ovCatMetric)}
            fmt={prefs.ovCatMetric === "value" ? money : num}
            onPick={(c) => navigate(`/report/current?q=${encodeURIComponent(c)}`)}
          />
        </Panel>
      );

    case "reconciliation": {
      const r = data.reconciliation;
      const tot = r.ok + r.warnings + r.errors || 1;
      return (
        <Panel
          title="Сверка остатков"
          actions={
            <button type="button" className="text-sm font-medium text-accent hover:underline" onClick={() => navigate("/reconciliation")}>
              подробнее →
            </button>
          }
        >
          {/* share bar — the same three counts, proportionally */}
          <div className="mb-4 flex h-2 overflow-hidden rounded-full bg-surface-2">
            <span className="bg-ok" style={{ width: `${(r.ok / tot) * 100}%` }} />
            <span className="bg-warn" style={{ width: `${(r.warnings / tot) * 100}%` }} />
            <span className="bg-danger" style={{ width: `${(r.errors / tot) * 100}%` }} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Pill tone="ok" label="Сходится" value={num(r.ok)} pct={Math.round((r.ok / tot) * 100)} />
            <Pill tone="warn" label="Расхождение" value={num(r.warnings)} />
            <Pill tone="danger" label="Ошибка" value={num(r.errors)} />
          </div>
          <p className="mt-4 text-xs text-text-2">
            {k.problems.critical + k.problems.warning === 0
              ? "Проблем в данных не найдено."
              : `Всего проблем в данных: ${num(k.problems.critical + k.problems.warning)}.`}
          </p>
        </Panel>
      );
    }

    default:
      return null;
  }
}

/* ---------- small pieces ---------- */

function kpiTile(id: string, d: DashboardData, days: number) {
  const k = d.kpis;
  const T: Record<
    string,
    {
      label: string;
      raw: number;
      fmt: (n: number) => string;
      unit?: string;
      hint?: string;
      danger?: boolean;
      go?: string;
      icon: string;
      tone: KpiTone;
      delta?: number;
    }
  > = {
    stock: {
      label: "Остаток на складе",
      raw: k.stock.value,
      fmt: num,
      unit: "ед.",
      hint: `за ${days} дн.`,
      delta: k.stock.delta,
      go: "/report/current",
      icon: "box",
      tone: "accent",
    },
    available: {
      label: "Доступно к отправке",
      raw: k.available.value,
      fmt: num,
      unit: "ед.",
      hint: `${num(k.available.reserved)} зарезервировано`,
      go: "/report/current",
      icon: "refresh",
      tone: "ok",
    },
    cover: {
      label: "Запас в днях",
      raw: d.health?.cover_days ?? 0,
      fmt: num,
      unit: "дн.",
      hint: d.health ? `при ~${fmtRate(d.health.shipped_per_day)} ед./день отгрузки` : "нет данных",
      go: "/report/current",
      icon: "clock",
      tone: "accent",
    },
    stock_value: {
      label: "Себестоимость остатка",
      raw: k.stock_value.value,
      fmt: money,
      unit: "сум",
      hint: "итого по складу",
      go: "/report/current",
      icon: "scale",
      tone: "accent",
    },
    sku: {
      label: "Товарных позиций",
      raw: k.sku.total,
      fmt: num,
      hint: `${num(k.sku.available)} в наличии · ${num(k.sku.empty)} пусто`,
      go: "/report/current",
      icon: "reports",
      tone: "accent",
    },
    problems: {
      label: "Проблемы",
      raw: k.problems.critical + k.problems.warning,
      fmt: num,
      danger: k.problems.critical > 0,
      hint: `${num(k.problems.critical)} критич. · ${num(k.problems.warning)} предупр.`,
      // straight to the most severe non-empty bucket instead of the unfiltered list
      go: k.problems.critical > 0 ? "/alerts?severity=critical" : k.problems.warning > 0 ? "/alerts?severity=warning" : "/alerts",
      icon: "alert",
      tone: k.problems.critical > 0 ? "danger" : k.problems.warning > 0 ? "warn" : "ok",
    },
  };
  if (id === "cover" && (!d.health || d.health.cover_days == null)) return undefined;
  return T[id];
}

const KPI_COLS: Record<number, string> = {
  1: "xl:grid-cols-1",
  2: "xl:grid-cols-2",
  3: "xl:grid-cols-3",
  4: "xl:grid-cols-4",
  5: "xl:grid-cols-5",
  // six tiles only fit one row on wide monitors; otherwise 3 × 2 keeps labels untruncated
  6: "xl:grid-cols-3 min-[1700px]:grid-cols-6",
};

/** "14:00" from an ISO timestamp, in the viewer's clock */
function hhmm(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

/** "0,3" / "4" / "12" — daily rates are often fractional for slow movers */
function fmtRate(n: number): string {
  return n >= 10 ? num(n) : n.toLocaleString("ru-RU", { maximumFractionDigits: 1 });
}

function coverLabel(days: number): string {
  if (days < 1) return "< 1 дн";
  return `${Math.round(days)} дн`;
}


/** "87 дн" / "5 мес" / "1.5 года" — how long a product has been sitting */
function idleLabel(days: number | null | undefined): string {
  if (days == null) return "—";
  if (days < 60) return `${days} дн`;
  if (days < 365) return `${Math.round(days / 30)} мес`;
  return `${(days / 365).toFixed(1)} года`;
}

function catRows(d: DashboardData, metric: "units" | "value") {
  return d.categories
    .map((c) => ({ category: c.category, units: metric === "value" ? c.value : c.units }))
    .sort((a, b) => b.units - a.units);
}

/** Grand total across all categories (API sends it on every row), so % is a share of the whole. */
function catTotal(rows: Array<{ total_units?: number; total_value?: number }>, metric: "units" | "value") {
  return metric === "value" ? rows[0]?.total_value : rows[0]?.total_units;
}

function shipCatRows(d: DashboardData, metric: "units" | "value") {
  return d.shipment_categories
    .map((c) => ({ category: c.category, units: metric === "value" ? c.value : c.units }))
    .sort((a, b) => b.units - a.units);
}

type KpiTone = "accent" | "ok" | "warn" | "danger";
const KPI_CHIP: Record<KpiTone, string> = {
  accent: "bg-accent-weak text-accent",
  ok: "bg-ok-weak text-ok",
  warn: "bg-warn-weak text-warn",
  danger: "bg-danger-weak text-danger",
};

/** One KPI card — identical structure for every metric: icon chip, name, big number, then either a
 * change indicator (when the API gives one) or a short explanatory line. */
function Kpi({
  label,
  raw,
  fmt,
  unit,
  hint,
  danger,
  icon,
  tone,
  delta,
  wideOnPhone,
  onClick,
}: {
  label: string;
  raw: number;
  fmt: (n: number) => string;
  unit?: string;
  hint?: string;
  danger?: boolean;
  icon?: string;
  tone: KpiTone;
  delta?: number;
  wideOnPhone?: boolean;
  onClick?: () => void;
}) {
  // Count up from the previous value (0 on first paint). `value` (the final string) drives the
  // font-size threshold so digits never resize mid-animation.
  const animated = useCountUp(raw);
  const value = fmt(raw);
  const displayValue = fmt(Math.round(animated));
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={cn(
        "group flex min-w-0 flex-col rounded-xl border border-border bg-surface p-4 text-left shadow-card sm:p-5",
        onClick && "hover:border-border-strong hover:shadow-card-hover",
        // a long sum (e.g. себестоимость) gets the full row on phones instead of wrapping digits
        (value.length > 10 || wideOnPhone) && "max-sm:col-span-2",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        {icon && (
          <span className={cn("grid h-9 w-9 flex-none place-items-center rounded-lg sm:h-10 sm:w-10", KPI_CHIP[tone])}>
            <Icon name={icon} size={18} />
          </span>
        )}
        {onClick && (
          <Icon name="chevron" size={16} className="mt-1 flex-none text-muted opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </div>
      <div className="mt-3 truncate text-xs font-medium text-text-2 sm:mt-4 sm:text-sm">{label}</div>
      {/* ru-RU grouping uses a NO-BREAK space, so a long money value is one unbreakable token —
          shrink past a length threshold, break-words as a last-resort fallback. */}
      <div
        className={cn(
          "mt-1 flex flex-wrap items-baseline gap-x-1 font-semibold leading-tight tracking-tight tnum",
          value.length > 13 ? "text-lg" : value.length > 10 ? "text-xl" : "text-xl sm:text-2xl",
        )}
      >
        <span className={cn("break-words", danger && "text-danger")}>{displayValue}</span>
        {unit && <span className="whitespace-nowrap text-sm font-normal text-text-2">{unit}</span>}
      </div>
      <div className="mt-auto flex flex-wrap items-center gap-x-1.5 gap-y-0.5 pt-2 text-xs text-text-2">
        {delta != null && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 rounded-md px-1.5 py-px font-semibold tnum",
              delta > 0 ? "bg-ok-weak text-ok" : delta < 0 ? "bg-danger-weak text-danger" : "bg-surface-2 text-text-2",
            )}
          >
            {delta > 0 ? "↑" : delta < 0 ? "↓" : "→"} {signed(delta)}
          </span>
        )}
        {hint && <span>{hint}</span>}
      </div>
    </button>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
  spark,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "danger";
  spark?: number[];
}) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-surface-3 px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="truncate text-sm font-medium text-text-2">{label}</div>
        {spark && spark.some((v) => v > 0) && <Sparkline values={spark} className="flex-none" />}
      </div>
      <div className={cn("mt-1 break-words text-lg font-semibold tracking-tight tnum", tone === "danger" && "text-danger")}>
        {value}
      </div>
      {sub && <div className="mt-0.5 break-words text-xs text-text-2 tnum">{sub}</div>}
    </div>
  );
}

/** Wraps TrendChart with a dual-handle zoom slider — drag either handle to focus on a sub-range
 * of the current window instead of always seeing the full 7/30/90 days (2026-09-12, explicit
 * request: "sliderlik qilaylik"). Resets to the full range whenever the underlying period changes
 * (new data with a different point count). */
function TrendCard({ points }: { points: TrendPoint[] }) {
  const lastIdx = Math.max(0, points.length - 1);
  const [range, setRange] = useState<[number, number]>([0, lastIdx]);
  useEffect(() => setRange([0, lastIdx]), [points.length, lastIdx]);
  const [lo, hi] = range;
  const shown = points.slice(lo, hi + 1);

  const fmtDay = (p?: TrendPoint) => (p ? `${p.date.slice(8)}.${p.date.slice(5, 7)}` : "");

  return (
    <>
      <TrendChart points={shown} />
      {points.length > 3 && (
        <div className="mt-3">
          <div className="relative h-3.5">
            <div className="absolute left-0 right-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-surface-2" />
            <div
              className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-accent/50"
              style={{ left: `${(lo / lastIdx) * 100}%`, right: `${100 - (hi / lastIdx) * 100}%` }}
            />
            <input
              type="range"
              className="range-dual"
              min={0}
              max={lastIdx}
              value={lo}
              onChange={(e) => setRange([Math.min(Number(e.target.value), hi - 1), hi])}
              aria-label="Начало диапазона"
            />
            <input
              type="range"
              className="range-dual"
              min={0}
              max={lastIdx}
              value={hi}
              onChange={(e) => setRange([lo, Math.max(Number(e.target.value), lo + 1)])}
              aria-label="Конец диапазона"
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-2xs text-muted">
            <span className="font-mono">{fmtDay(points[lo])}</span>
            {(lo !== 0 || hi !== lastIdx) && (
              <button type="button" onClick={() => setRange([0, lastIdx])} className="font-medium text-accent hover:underline">
                Сбросить масштаб
              </button>
            )}
            <span className="font-mono">{fmtDay(points[hi])}</span>
          </div>
        </div>
      )}
    </>
  );
}

function Pill({ tone, label, value, pct }: { tone: "ok" | "warn" | "danger"; label: string; value: string; pct?: number }) {
  const dot = tone === "ok" ? "bg-ok" : tone === "warn" ? "bg-warn" : "bg-danger";
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-xs text-text-2">
        <span className={cn("h-2 w-2 flex-none rounded-sm", dot)} />
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-0.5 text-lg font-semibold tracking-tight tnum">
        {value}
        {pct != null && <span className="ml-1 text-xs font-normal text-text-2">{pct}%</span>}
      </div>
    </div>
  );
}

function MiniList<T>({
  rows,
  render,
  empty,
  navigate,
}: {
  rows: T[];
  render: (r: T) => { name: string; meta?: string; value: string; sub?: string; danger?: boolean; warn?: boolean; go?: string };
  empty: string;
  navigate: (to: string) => void;
}) {
  if (!rows.length) return <div className="py-8 text-center text-sm text-text-2">{empty}</div>;
  return (
    <div className="-my-1.5 divide-y divide-border">
      {rows.map((r, i) => {
        const c = render(r);
        return (
          <button
            key={i}
            type="button"
            onClick={() => c.go && navigate(c.go)}
            className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-md px-2 py-2.5 text-left hover:bg-surface-3"
          >
            <span className="grid h-6 w-6 flex-none place-items-center rounded-md bg-surface-2 text-2xs font-semibold text-text-2 tnum">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{c.name}</div>
              {c.meta && <div className="truncate text-xs text-text-2">{c.meta}</div>}
            </div>
            <div className="flex-none text-right">
              <div className={cn("text-sm font-semibold tnum", c.danger ? "text-danger" : c.warn && "text-warn")}>{c.value}</div>
              {c.sub && <div className="text-xs text-text-2 tnum">{c.sub}</div>}
            </div>
          </button>
        );
      })}
    </div>
  );
}
